import { useLayoutEffect } from 'react';
import './navigation.css';
import { useLocation } from 'react-router-dom';

let overlay: HTMLDivElement | undefined;
let previousFocus: HTMLElement | null = null;
let inertRoots: HTMLElement[] = [];
let leaving = false;
let revision = 0;
let redirectGeneration = 0;

/** Cover the current host before menus, sessions or routes change. */
export function beginNavigation(source?: Element | null) {
  revision++;
  if (overlay?.isConnected) return;
  const theme = source?.closest('[class*="bcTheme-"]') ?? document.querySelector('[class*="bcTheme-"], .hub, .public-shell') ?? document.body;
  const styles = getComputedStyle(theme);
  const color = (name: string, fallback: string) => styles.getPropertyValue(name).trim() || fallback;
  overlay = document.createElement('div');
  overlay.dataset.navigationLoading = 'true';
  overlay.className = 'navigation-loading';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Loading');
  overlay.setAttribute('aria-busy', 'true');
  overlay.tabIndex = -1;
  overlay.style.setProperty('--navigation-bg', color('--bg', '#f6f8fc'));
  overlay.style.setProperty('--navigation-surface', color('--surface', '#ffffff'));
  overlay.style.setProperty('--navigation-text', color('--text', '#0f172a'));
  overlay.style.setProperty('--navigation-primary', color('--primary', '#2563eb'));
  const card = document.createElement('div');
  card.setAttribute('role', 'status');
  card.className = 'navigation-loading-card';
  card.setAttribute('aria-label', 'Loading');
  const spinner = document.createElement('span');
  spinner.className = 'navigation-loading-spinner';
  spinner.setAttribute('aria-hidden', 'true');
  card.append(spinner);
  overlay.append(card);
  previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  inertRoots = Array.from(document.body.children).filter((child): child is HTMLElement => child instanceof HTMLElement && !child.inert && !['SCRIPT', 'STYLE'].includes(child.tagName));
  inertRoots.forEach(root => { root.inert = true; });
  document.body.append(overlay);
  overlay.focus({ preventScroll: true });
}

export function endNavigation() {
  revision++;
  redirectGeneration++;
  leaving = false;
  overlay?.remove(); overlay = undefined;
  inertRoots.forEach(root => { root.inert = false; }); inertRoots = [];
  if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
  previousFocus = null;
}

/** Let the loading screen paint before the browser leaves this host. */
export function redirectTo(url: string, replace = false) {
  beginNavigation(); leaving = true;
  const generation = ++redirectGeneration;
  requestAnimationFrame(() => requestAnimationFrame(() => {
    if (generation !== redirectGeneration) return;
    try { if (replace) window.location.replace(url); else window.location.assign(url); }
    catch (error) { endNavigation(); throw error; }
  }));
}

export function NavigationFeedback({ resolveDestinations = false }: { resolveDestinations?: boolean }): null {
  const location = useLocation();
  useLayoutEffect(() => {
    const click = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!(anchor instanceof HTMLAnchorElement) || anchor.download || (anchor.target && anchor.target !== '_self')) return;
      const url = new URL(anchor.href, window.location.href);
      if (!['http:', 'https:'].includes(url.protocol) || url.href === window.location.href || (url.origin === window.location.origin && url.pathname === window.location.pathname && url.search === window.location.search)) return;
      beginNavigation(anchor);
      if (url.origin !== window.location.origin) {
        leaving = true;
        const generation = redirectGeneration;
        // Preserve existing navigation guards and let the anchor navigate normally.
        queueMicrotask(() => { if (event.defaultPrevented && redirectGeneration === generation) endNavigation(); });
      } else {
        const current = revision;
        // A cancelled link (e.g. an unsaved-changes prompt) must remain usable.
        setTimeout(() => { if (revision === current && !leaving && window.location.pathname !== url.pathname) endNavigation(); }, 0);
      }
    };
    const restored = () => endNavigation();
    document.addEventListener('click', click, true);
    window.addEventListener('pageshow', restored);
    return () => { document.removeEventListener('click', click, true); window.removeEventListener('pageshow', restored); endNavigation(); };
  }, []);
  useLayoutEffect(() => {
    // These routes resolve a destination asynchronously and clear on failure or local content.
    if (resolveDestinations && /^\/(o|launch)\//.test(location.pathname)) { beginNavigation(); return; }
    const current = revision;
    const frame = requestAnimationFrame(() => requestAnimationFrame(() => { if (revision === current && !leaving) endNavigation(); }));
    return () => cancelAnimationFrame(frame);
  }, [location.key, location.pathname, resolveDestinations]);
  return null;
}
